import type { Updater } from "@tanstack/vue-table"
import type { ClassValue } from "clsx"
import type { Ref } from "vue"
import { clsx } from "clsx"
import { twMerge } from "tailwind-merge"
import type { DepotCompanyDeletionOutcome } from "@/stores/depot/company.store"

export function cn(...inputs: ClassValue[]) {
  return twMerge(clsx(inputs))
}

export function valueUpdater<T extends Updater<any>>(updaterOrValue: T, ref: Ref) {
  ref.value
    = typeof updaterOrValue === "function"
      ? updaterOrValue(ref.value)
      : updaterOrValue
}

export function formatRelativeDate({ days = 0, months = 0 }: { days?: number; months?: number }) {
  const targetDate = new Date()

  if (months > 0) {
    targetDate.setMonth(targetDate.getMonth() - months)
  }

  if (days > 0) {
    targetDate.setDate(targetDate.getDate() - days)
  }

  return targetDate.toISOString().split('T')[0]
}

/**
 * The review window the profile insights analysis runs over. Shared so the
 * request and the "analysing…" label cannot name different windows.
 */
export function insightsWindow() {
  return {
    from_date: formatRelativeDate({ months: 3 }),
    // No offset: today.
    to_date: formatRelativeDate({})
  }
}

/**
 * Profile UUIDs from a site's channels.
 *
 * Read tolerantly because the shape is not typed at either end: the company
 * sites and site detail endpoints both return `any`, `View-Site.vue` reads
 * `profile_id` off the detail response, and the typed `DepotChannel` names the
 * same concept `profile_uuid`. Whether the two endpoints agree is not knowable
 * from source, so accept either and drop anything carrying neither — an empty
 * result surfaces at the caller's empty-state guard rather than posting a list
 * of undefined.
 */
export function channelProfileUuids(channels: any[] | null | undefined): string[] {
  if (!Array.isArray(channels)) {
    return []
  }

  return channels
    .map((channel) => {return channel?.profile_uuid ?? channel?.profile_id})
    .filter((uuid): uuid is string => {return Boolean(uuid)})
}

/**
 * User-facing wording for a failed company deletion.
 *
 * Shared by the listing row menu and the company page rather than duplicated
 * alongside their dialogs: both map the same statuses, and letting the wording
 * drift would mean the same refusal reads differently depending on where the
 * admin triggered it.
 *
 * A 404 means the company is already gone. A 409 is the server refusing the
 * delete — either sites still hang off it or it was written to mid-delete — and
 * its own message says which, so it is passed through. Anything else is a
 * failure the admin can only retry.
 */
export function deletionFailureMessage(outcome: DepotCompanyDeletionOutcome): string {
  if (outcome.status === 404) {
    return "This company could not be found. It may already have been deleted."
  }

  if (outcome.status === 409 && outcome.message) {
    return outcome.message
  }

  return "Something went wrong deleting this company. Please try again."
}
